// static/js/components/ui/ImageResult.jsx

import React, { useState } from 'react';
import { Loader } from 'lucide-react';
import { Download } from 'lucide-react'; // for downloading images

/**
 * ImageResult Component
 *
 * This component is responsible for displaying image results within the UI. It features functionalities
 * to process image paths, handle image downloads securely without exposing user-specific details, and
 * manage the loading and error states of images. The component ensures that images are displayed with
 * appropriate error handling and provides an option to download the image directly from a controlled
 * endpoint. It uses React's state management to handle the visual representation of loading states
 * and potential errors during image retrieval.
 *
 * Features:
 * - Processes image paths to ensure secure URL construction.
 * - Handles image downloads to avoid direct exposure of sensitive file paths.
 * - Manages loading and error states visually with feedback to users.
 * - Secures image interaction by preventing context menu access and drag functionality.
 * - Optional header display can be controlled via props.
 *
 * Usage:
 * <ImageResult src="path/to/image.jpg" alt="Descriptive text for the image" showHeader={false} />
 *
 * The component expects `src` for the image source URL and `alt` for alternative text describing the image.
 * It displays a loading spinner until the image is fully loaded, and in case of an error in loading the image,
 * it presents an error message to the user.
 */

/**
 * Component for displaying image results from tool functions.
 * Handles loading states and errors.
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for the image
 * @param {boolean} props.showHeader - Whether to show the header with title and download button (default: true)
 */
const ImageResult = ({ src, alt, showHeader = true }) => { // showHeader=true displays "Generated Image" and download button

  // Process the image path to get the correct URL
  const processImagePath = (imagePath) => {
    // Extract just the filename from the full path
    const filename = imagePath.split('/').pop();
    // Return the correct URL path to access the image
    return `/webapp_user_images/${filename}`;
  };

  // Process the source URL
  const imageUrl = processImagePath(src);

  // Handles image download through download endpoint, so as to not expose the user id
  const handleImageDownload = (filepath) => {
    const filename = filepath.split('/').pop();
    window.open(`/download_tool_result/${filename}`, '_blank');
  };

  // State management for image loading status
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  return (
    <div className="relative mt-4 border rounded-lg p-4 bg-primary-700/10">

      {/* Container for image and controls - only shown if showHeader is true */}
      {showHeader && ( // Added conditional rendering based on showHeader prop
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-primary-text font-medium">Generated Image</h4>
          {/* Download button */}
          <button
            onClick={() => handleImageDownload(src)}
            className="p-2 text-primary-text hover:bg-primary-700/20 rounded-lg transition-colors"
            title="Download image"
          >
            <Download size={20} />
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <Loader className="animate-spin" />
        </div>
      )}

      {/* Image element */}
      <div className="relative max-h-[500px] overflow-auto">
        <img
          src={imageUrl}
          alt={alt}
          className="max-w-full rounded-lg select-none"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setError(`Failed to load image: ${imageUrl}`);
            console.error('Image load failed:', imageUrl);
          }}
          style={{ display: isLoading ? 'none' : 'block' }}
          onContextMenu={(e) => e.preventDefault()}  // Prevents right-click menu to avoid users having direct access to temp folder
          draggable="false"  // Prevents image dragging
        />
      </div>
  
      {/* Error message */}
      {error && (
        <div className="text-red-500 text-sm mt-2">{error}</div>
      )}
    </div>
  );
};

export default ImageResult;